import { QuoteCurrencyList, UsdCurrency } from './constants/currency';
import { isNullable } from './nullable';
import Num from './num';

const NumberRegex = /^\d/;

/**
 * 심볼을 base 와 quote currency 로 쪼갠다.
 *
 * quote currency 목록을 열거하지 않고 명명 규약으로 해석한다: quote currency 는 USD 계열
 * (USDT / USD1 / USDC …)이므로 **마지막 'USD' 부터가 quote** 다. 목록 기반으로 하면 상장폐지된
 * 통화를 목록에서 빼는 순간 그 심볼이 파싱에 실패해 정렬이 무너지는데(`BTCUSD1` → base 가
 * 'BTCUSD1' 이 되어 'BTC' 와 묶이지 않음), 히스토리 심볼 선택기처럼 과거 심볼이 계속 노출되는
 * 화면이 있어 그 방식은 쓸 수 없다.
 *
 * 마지막 'USD' 를 쓰므로 base 가 USD 로 끝나는 심볼도 올바르게 쪼갠다 (`SUSDUSDT` → SUSD + USDT).
 *
 * 한계: quote currency 가 USD 계열이 아닌 심볼은 규약 밖이다. quote 를 못 찾으면(`ETHBTC`)
 * currency 가 '' 이 되어 심볼 문자열 비교로 폴백하고, base 에 USD 가 섞여 있으면(`SUSDBTC`)
 * 엉뚱하게 쪼개진다(S + USDBTC). 둘 다 정렬 순서만 어긋나고 동작은 깨지지 않는다.
 * 비-USD quote 를 실제로 상장하면 이 함수의 규약부터 다시 정해야 한다.
 */
const parseSymbol = (symbol: string) => {
  const isSpot = !symbol.endsWith('.PERP');
  const baseSymbol = isSpot ? symbol : symbol.replace('.PERP', '');
  const quoteStart = baseSymbol.lastIndexOf(UsdCurrency);
  // 못 찾음(-1) 또는 심볼 전체가 quote 로 시작(0, base 가 없음)
  if (quoteStart <= 0) return { base: baseSymbol, currency: '', isSpot };
  return { base: baseSymbol.slice(0, quoteStart), currency: baseSymbol.slice(quoteStart), isSpot };
};

export const instrumentsSort = (a: string, b: string) => {
  const isAStartNumber = NumberRegex.test(a);
  const isBStartNumber = NumberRegex.test(b);
  if (isAStartNumber && !isBStartNumber) return 1;
  if (!isAStartNumber && isBStartNumber) return -1;

  const parsedA = parseSymbol(a);
  const parsedB = parseSymbol(b);

  if (parsedA.base === parsedB.base && parsedA.currency && parsedB.currency) {
    if (!parsedA.isSpot && parsedB.isSpot) return -1;
    if (parsedA.isSpot && !parsedB.isSpot) return 1;

    // 우선순위는 QuoteCurrencyList 의 배열 순서 (USDT 가 0번이라 항상 먼저).
    // 목록에 없는 quote currency(상장폐지 등)는 뒤로 보내고, 그들끼리는 알파벳 순.
    const indexA = QuoteCurrencyList.indexOf(parsedA.currency as (typeof QuoteCurrencyList)[number]);
    const indexB = QuoteCurrencyList.indexOf(parsedB.currency as (typeof QuoteCurrencyList)[number]);

    const allInList = indexA !== -1 && indexB !== -1;
    if (allInList) return indexA - indexB;
    if (indexA !== -1 && indexB === -1) return -1;
    if (indexA === -1 && indexB !== -1) return 1;

    return parsedA.currency.localeCompare(parsedB.currency);
  }

  return a.localeCompare(b);
};

interface SortablePosition {
  symbol: string;
}

export const positionOrderSort = (a: SortablePosition, b: SortablePosition) => {
  return a.symbol.localeCompare(b.symbol);
};

interface Options {
  type: 'string' | 'number' | 'bignumber';
  order: 'asc' | 'desc';
}

export const sortingHelper = <T extends string | number>(a: T, b: T, options: Options) => {
  if (isNullable(a) || isNullable(b)) return 0;
  const compareA = options.order === 'asc' ? a : b;
  const compareB = options.order === 'asc' ? b : a;

  if (options.type === 'number') {
    if (compareA < compareB) return -1;
    if (compareA > compareB) return 1;
    return 0;
  }
  if (options.type === 'bignumber') {
    if (Num(compareA).isLessThan(compareB)) return -1;
    if (Num(compareA).isGreaterThan(compareB)) return 1;
    return 0;
  }
  return (compareA as string).localeCompare(compareB as string);
};
